import {Component, ElementRef, HostListener, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DatePipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {NotificationService} from '@/app/services/notification.service';
import {Notification, NotificationType} from '@/app/core/models/notification';

interface Particle {
	id: number;
	tx: string;
	ty: string;
	rot: string;
}

@Component({
	selector: 'peps-notification-center',
	imports: [TranslatePipe, DatePipe, RouterLink],
	templateUrl: './notification-center.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './notification-center.component.css',
})
export class NotificationCenterComponent implements OnInit {

	private readonly el: ElementRef = inject(ElementRef);
	private readonly notificationService: NotificationService = inject(NotificationService);

	isOpen = false;
	particles: Particle[] = [];

	private particleCounter = 0;

	/** Signal partagé côté service — deux instances de ce composant (desktop/mobile) restent synchro sur un seul fetch. */
	get notifications(): Notification[] {
		return this.notificationService.notifications();
	}

	ngOnInit(): void {
		this.notificationService.fetchNotifications();
	}

	get unreadCount(): number {
		return this.notifications.filter(n => !n.read).length;
	}

	@HostListener('document:click', ['$event.target'])
	onClickOutside(target: EventTarget | null) {
		if (target instanceof Node && !this.el.nativeElement.contains(target)) {
			this.isOpen = false;
		}
	}

	toggle(event: MouseEvent) {
		event.stopPropagation();
		if (!this.isOpen) {
			this.spawnParticles();
		}
		this.isOpen = !this.isOpen;
	}

	getNotificationKey(notif: Notification): string {
		if (notif.type === 'PREDICTION_RESULT') {
			const points = (notif.metadata?.['pointsEarned'] as number) ?? 0;
			return `notification.type.PREDICTION_RESULT.${points > 0 ? 'won' : 'lost'}`;
		}
		return `notification.type.${notif.type}`;
	}

	/**
	 * Route cible du bouton « Voir » selon le type de notification :
	 * - boosters / cartes / PepsCoins / badge  → profil
	 * - quête validée / montée de rang / palier → battlepeps
	 * - résultat de prono                       → page des pronos
	 */
	private static readonly ROUTE_BY_TYPE: Partial<Record<NotificationType, string>> = {
		BOOSTER_PURCHASED: '/profil',
		BOOSTER_RECEIVED: '/profil',
		WEEKLY_BOOSTER_OFFERED: '/profil',
		CARD_RECEIVED: '/profil',
		CARD_SOLD: '/profil',
		CARD_PURCHASED: '/profil',
		COIN_CREDIT: '/profil',
		BADGE_UNLOCKED: '/profil',
		QUEST_VALIDATED: '/battlepeps',
		BATTLEPEPS_RANK_UP: '/battlepeps',
		BATTLEPEPS_TIER_REACHED: '/battlepeps',
		PREDICTION_RESULT: '/bet',
	};

	notificationLink(notif: Notification): string | null {
		return NotificationCenterComponent.ROUTE_BY_TYPE[notif.type] ?? null;
	}

	/** Clic sur « Voir » : marque la notif comme lue puis ferme la modale (le routerLink gère la navigation). */
	onView(notif: Notification): void {
		if (!notif.read) {
			this.notificationService.markAsRead(notif.id).subscribe();
		}
		this.isOpen = false;
	}

	markAllAsRead() {
		// Le service met à jour le signal partagé (voir markAllAsRead côté service) — rien à faire ici.
		this.notificationService.markAllAsRead().subscribe();
	}

	private spawnParticles() {
		const count = 8;
		const particles: Particle[] = [];
		for (let i = 0; i < count; i++) {
			const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
			const distance = 28 + Math.random() * 20;
			particles.push({
				id: this.particleCounter++,
				tx: `${Math.cos(angle) * distance}px`,
				ty: `${Math.sin(angle) * distance}px`,
				rot: `${Math.floor(Math.random() * 360)}deg`,
			});
		}
		this.particles = particles;
		setTimeout(() => { this.particles = []; }, 700);
	}
}
